import "./Install.scss";

import React, { useEffect, useMemo, useRef, useState } from "react";

import AppButton from "../commons/buttons/AppButton";
import ReportHeader from "../pages/report-full/components/ReportHeader";

function Install() {
  const deferredPromptRef = useRef(null);
  const [canInstall, setCanInstall] = useState(false);

  const [showIosPill, setShowIosPill] = useState(false);
  const [showIosOverlay, setShowIosOverlay] = useState(false);

  const dialogRef = useRef(null);
  const lastFocusedRef = useRef(null);

  const pillTimerRef = useRef(null);
  const overlayTimerRef = useRef(null);

  const STORAGE_HIDE = "pwa_install_hide_until";
  const STORAGE_DONE = "pwa_install_completed";
  const HIDE_FOR_DAYS = 7;

  const isStandalone = useMemo(() => {
    return (
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true
    );
  }, []);

  const isIOSSafariEligible = useMemo(() => {
    const ua = (navigator.userAgent || "").toLowerCase();
    const isIOS =
      /iphone|ipad|ipod/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isSafari = /safari/.test(ua) && !/crios|fxios|chrome|firefox/.test(ua);

    let completed = false;
    let hideUntil = 0;
    try {
      completed = localStorage.getItem(STORAGE_DONE) === "1";
      hideUntil = parseInt(localStorage.getItem(STORAGE_HIDE) || "0", 10);
    } catch {}

    const now = Date.now();
    return isIOS && isSafari && !isStandalone && !completed && !(hideUntil && now < hideUntil);
  }, [isStandalone]);

  // --- Android/Chrome: captura antes do prompt
  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      deferredPromptRef.current = event;
      setCanInstall(true);
    };

    const onAppInstalled = () => {
      try {
        localStorage.setItem(STORAGE_DONE, "1");
        localStorage.removeItem(STORAGE_HIDE);
      } catch {}
      deferredPromptRef.current = null;
      setCanInstall(false);
      setShowIosPill(false);
      setShowIosOverlay(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  // --- força reavaliação do "instalável" sem precisar reload (ajuda em alguns devices)
  useEffect(() => {
    let t = null;

    async function check() {
      if (isStandalone) {
        setCanInstall(false);
        deferredPromptRef.current = null;
        return;
      }

      // não garante “installable”, mas ajuda a não depender só do reload
      // (em alguns Androids o evento dispara depois de um tempo)
      t = setTimeout(() => {
        // se o evento já chegou, canInstall já ficou true
        // aqui só damos mais chance de UI estar atualizada
        setCanInstall(!!deferredPromptRef.current);
      }, 900);
    }

    check();
    return () => t && clearTimeout(t);
  }, [isStandalone]);

  // --- UX iOS (pílula + overlay)
  useEffect(() => {
    if (!isIOSSafariEligible) return;

    pillTimerRef.current = setTimeout(() => setShowIosPill(true), 1800);
    overlayTimerRef.current = setTimeout(() => {
      setShowIosOverlay((prev) => prev || showIosPill);
    }, 6200);

    return () => {
      if (pillTimerRef.current) clearTimeout(pillTimerRef.current);
      if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
    };
  }, [isIOSSafariEligible, showIosPill]);

  // --- foco acessível no diálogo iOS
  useEffect(() => {
    if (!showIosOverlay) return;

    lastFocusedRef.current = document.activeElement;
    requestAnimationFrame(() => dialogRef.current?.focus());

    const onKey = (e) => {
      if (e.key === "Escape") handleIosLater();
    };

    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (lastFocusedRef.current && lastFocusedRef.current.focus) {
        lastFocusedRef.current.focus();
      }
    };
  }, [showIosOverlay]);

  async function handleInstallClick() {
    const dp = deferredPromptRef.current;
    if (!dp) return;

    dp.prompt();
    try {
      await dp.userChoice;
    } catch {}

    // após escolher, o evento não pode ser reusado
    deferredPromptRef.current = null;
    setCanInstall(false);
  }

  function handleIosOpenOverlay() {
    setShowIosPill(false);
    setShowIosOverlay(true);
  }

  function handleIosOk() {
    try {
      localStorage.setItem(STORAGE_DONE, "1");
    } catch {}
    setShowIosOverlay(false);
  }

  function handleIosLater() {
    const until = Date.now() + HIDE_FOR_DAYS * 24 * 60 * 60 * 1000;
    try {
      localStorage.setItem(STORAGE_HIDE, String(until));
    } catch {}
    setShowIosOverlay(false);
    setShowIosPill(false);
  }

  return (
    <>
      <ReportHeader name="Instale o HallApp" />

      <main className="install" aria-labelledby="installTitle" style={{marginTop: '60px'}}>
        {/* Hero */}
        <section className="installHero">
          <div className="installHeroCard">
            <div className="installBrand">
              <div className="installBrandLogo" aria-hidden="true">
                H
              </div>
              <div>
                <h1 id="installTitle" className="installTitle">
                  Instale o HallApp
                </h1>
                <p className="installSubtitle">
                  Tenha o HallApp como app na sua tela inicial: mais rápido e prático.
                </p>
              </div>
            </div>

            <div className="installBadges" aria-label="Benefícios">
              <span className="installBadge">Tela cheia</span>
              <span className="installBadge">Atalho na tela</span>
              <span className="installBadge">Mais fluido</span>
            </div>

            {/* CTA Android (quando disponível) */}
            {canInstall && !isStandalone && (
              <div className="installCta">
                <AppButton name="Instalar agora" width="100%" onClick={handleInstallClick} />
                <div className="installCtaHint">
                  Se não aparecer pra você, use o menu do navegador e escolha “Adicionar à Tela inicial”.
                </div>
              </div>
            )}

            {isStandalone && (
              <div className="installOk">
                ✅ O HallApp já está instalado no seu dispositivo.
              </div>
            )}
          </div>
        </section>

        {/* Cards por plataforma */}
        {!isStandalone && (
          <section className="installGrid">
            {/* Android */}
            <article className="installCard" aria-label="Android e Chrome">
              <header className="installCardHeader">
                <div className="installCardIcon" aria-hidden>
                  🤖
                </div>
                <div>
                  <h2 className="installCardTitle">Android / Chrome</h2>
                  <p className="installCardDesc">Forma mais rápida: pelo botão de instalar quando disponível.</p>
                </div>
              </header>

              <ol className="installSteps">
                <li>Abra o menu do Chrome (⋮).</li>
                <li>Toque em “Adicionar à Tela inicial”.</li>
                <li>Confirme para criar o app do HallApp.</li>
              </ol>
            </article>

            {/* iOS */}
            <article className="installCard" aria-label="iPhone e Safari">
              <header className="installCardHeader">
                <div className="installCardIcon" aria-hidden>
                  🍎
                </div>
                <div>
                  <h2 className="installCardTitle">iPhone — Safari</h2>
                  <p className="installCardDesc">No iPhone é pelo botão “Compartilhar”.</p>
                </div>
              </header>

              <ol className="installSteps">
                <li>Abra esta página no Safari.</li>
                <li>Toque em Compartilhar (quadrado com seta).</li>
                <li>“Adicionar à Tela de Início” e confirme.</li>
              </ol>

              <div className="installNote">
                Dica: depois disso, o HallApp abre em tela cheia como um app.
              </div>
            </article>
          </section>
        )}

        <p className="installFootnote" role="note">
          Alguns navegadores não exibem o botão de instalação. Mesmo assim, você consegue criar um atalho pelo menu
          (“Adicionar à Tela inicial”).
        </p>
      </main>

      {/* iOS: pill flutuante */}
      {isIOSSafariEligible && showIosPill && (
        <button
          onClick={handleIosOpenOverlay}
          aria-label="Ver instruções para adicionar à Tela de Início"
          className="installPill"
        >
          Toque em Compartilhar → “Adicionar à Tela de Início”
        </button>
      )}

      {/* iOS: overlay */}
      {isIOSSafariEligible && showIosOverlay && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="pwaInstallDialogTitle"
          className="installOverlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleIosLater();
          }}
        >
          <div className="installDialog" ref={dialogRef} tabIndex={-1}>
            <header className="installDialogHeader">
              <span className="installDialogBadge" aria-hidden>
                ✓
              </span>
              <h2 id="pwaInstallDialogTitle">Adicionar à Tela de Início</h2>
            </header>

            <div className="installDialogBody">
              No iPhone, abra pelo <b>Safari</b>, toque em <b>Compartilhar</b> e escolha
              <b> “Adicionar à Tela de Início”</b>.
              <div className="installDialogTip">Isso cria o ícone do HallApp e ele abre em tela cheia.</div>
            </div>

            <footer className="installDialogActions">
              <button className="btnSecondary" onClick={handleIosLater}>
                Depois
              </button>
              <button className="btnPrimary" onClick={handleIosOk}>
                Entendi
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

export default Install;
